import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { ChevronRight, Info, RefreshCw } from "lucide-react";
import clsx from "clsx";
import { statusOf, GLOSSARY, timeAgo } from "../../lib/copy";
import { useAuth } from "../../lib/api";
import { Button, Input, Label, Modal, Mono } from "../ui";

const TONE = { green: "cx-badge-green", blue: "cx-badge-blue", amber: "cx-badge-yellow", red: "cx-badge-red", gray: "cx-badge-gray" };
const DOT = { green: "cx-status-dot-ready", blue: "cx-status-dot-pending", amber: "cx-status-dot-pending", red: "cx-status-dot-error", gray: "cx-status-dot-offline" };

/** One status pill for every object type, driven by STATUS_MAP in lib/copy.js. */
export function StatusPill({ kind, status, suffix, testid, className }) {
  const s = statusOf(kind, status);
  return (<span className={clsx("cx-badge inline-flex items-center gap-1.5 whitespace-nowrap", TONE[s.tone], className)} data-testid={testid} data-tone={s.tone} title={s.line}>
    <span className={clsx("cx-status-dot", DOT[s.tone])} />{s.label}{suffix ? ` ${suffix}` : ""}
  </span>);
}

/** Row of linked chips: Machine: ui-box-01 › Cluster: ui-box-01 › Deployments: 2 */
export function RelationshipRibbon({ items, testid = "relationship-ribbon" }) {
  const list = (items || []).filter(Boolean);
  if (!list.length) return null;
  return (<span className="flex flex-wrap items-center gap-1.5 text-xs" data-testid={testid}>
    {list.map((it, i) => {
      const inner = <><span className="text-cx-muted">{it.label}:</span> <span className="font-medium">{it.value}</span></>;
      return (<React.Fragment key={i}>
        {i > 0 && <ChevronRight size={12} className="text-cx-muted" />}
        {it.to ? <Link to={it.to} className="cx-badge cx-badge-gray hover:border-cx-emerald/60 transition-colors" data-testid={`${testid}-${i}`}>{inner}</Link> : <span className="cx-badge cx-badge-gray" data-testid={`${testid}-${i}`}>{inner}</span>}
      </React.Fragment>);
    })}
  </span>);
}

/** Shared polling beat. Data never blanks: `data` keeps the last value while `loading`. */
export function usePolling(fn, { intervalMs = 30000, deps = [] } = {}) {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [at, setAt] = useState(null);
  const [tick, setTick] = useState(0);
  const alive = useRef(true);
  const run = useCallback(async () => {
    setLoading(true);
    try { const d = await fn(); if (alive.current) { setData(d); setError(null); setAt(Date.now()); } }
    catch (e) { if (alive.current) setError(e); }
    finally { if (alive.current) setLoading(false); }
  }, deps);
  useEffect(() => { alive.current = true; run(); const t = setInterval(run, intervalMs); const tk = setInterval(() => setTick((x) => x + 1), 1000); return () => { alive.current = false; clearInterval(t); clearInterval(tk); }; }, [run, intervalMs]);
  const nextIn = at ? Math.max(0, Math.round((at + intervalMs - Date.now()) / 1000)) : null;
  return { data, error, loading, at, nextIn, refresh: run, tick };
}

export function RefreshStatus({ at, nextIn, loading, onRefresh, live = false, testid = "refresh-status" }) {
  return (<div className="flex items-center gap-2 text-xs text-cx-muted tabular-nums" data-testid={testid}>
    {live && <span className="inline-flex items-center gap-1 text-cx-emerald"><span className="cx-status-dot cx-status-dot-ready" /> Live</span>}
    <span>{at ? `updated ${timeAgo(new Date(at).toISOString())}` : "loading…"}{nextIn != null && ` · refresh in ${nextIn} s`}</span>
    <button className="cx-btn cx-btn-ghost !p-1" onClick={onRefresh} aria-label="Refresh" data-testid={`${testid}-btn`}><RefreshCw size={12} className={loading ? "animate-spin" : ""} /></button>
  </div>);
}

/** Typed confirmation (type the name or STOP) — §13.3 */
export function ConfirmTyped({ open, onOpenChange, title, desc, word, onConfirm, busy, confirmLabel = "Confirm", testid = "confirm-typed", children }) {
  const [typed, setTyped] = useState("");
  useEffect(() => { if (!open) setTyped(""); }, [open]);
  return (<Modal open={open} onOpenChange={onOpenChange} title={title} desc={desc} testid={testid}
    footer={<><Button variant="secondary" onClick={() => onOpenChange(false)} data-testid={`${testid}-cancel`}>Cancel</Button><Button variant="danger" disabled={typed !== word || busy} onClick={onConfirm} data-testid={`${testid}-submit`}>{busy ? "Working…" : confirmLabel}</Button></>}>
    {children}
    <div className="mt-3"><Label>Type <Mono>{word}</Mono> to confirm</Label><Input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid={`${testid}-input`} autoFocus /></div>
  </Modal>);
}

/** Impact confirmation: a plain statement of what happens — §13.3 */
export function ConfirmImpact({ open, onOpenChange, title, impact, onConfirm, busy, confirmLabel = "Confirm", danger = false, testid = "confirm-impact", children }) {
  return (<Modal open={open} onOpenChange={onOpenChange} title={title} testid={testid}
    footer={<><Button variant="secondary" onClick={() => onOpenChange(false)} data-testid={`${testid}-cancel`}>Cancel</Button><Button variant={danger ? "danger" : "primary"} disabled={busy} onClick={onConfirm} data-testid={`${testid}-submit`}>{busy ? "Working…" : confirmLabel}</Button></>}>
    <p className="text-sm" data-testid={`${testid}-impact`}>{impact}</p>{children}
  </Modal>);
}

/** Technical details switch — per page, remembered per user (localStorage keyed by user id); default ON for admin/operator. */
export function useTechnicalDetails(page) {
  const { user } = useAuth();
  const key = `cx_tech_${user?.id || user?.email || "anon"}_${page}`;
  const def = ["admin", "operator"].includes(user?.role);
  const [on, setOn] = useState(() => { const v = localStorage.getItem(key); return v == null ? def : v === "1"; });
  useEffect(() => { localStorage.setItem(key, on ? "1" : "0"); }, [key, on]);
  return [on, setOn];
}

/** THE shared toggle. Controlled only: `checked` + `onChange(next)`. The whole row is one <button>, so label clicks never double-fire. */
export function Switch({ checked, onChange, disabled = false, label, hint, labelFirst = false, size = "md", className, testid }) {
  const on = !!checked;
  const track = <span className={clsx("cx-switch", size === "sm" && "cx-switch-sm")} aria-hidden="true"><span className="cx-switch-thumb" /></span>;
  return (<button type="button" role="switch" aria-checked={on} disabled={disabled} data-state={on ? "checked" : "unchecked"} data-testid={testid}
    onClick={() => { if (!disabled) onChange(!on); }}
    className={clsx("cx-switch-row", disabled && "cx-switch-disabled", !label && "!gap-0", className)}>
    {labelFirst && label && <span className="cx-switch-label">{label}</span>}
    {track}
    {!labelFirst && label && <span className="cx-switch-label">{label}</span>}
    {hint && <span className="cx-switch-hint">{hint}</span>}
  </button>);
}

export { Segmented } from "../controls";

export function TechnicalDetailsSwitch({ on, onChange, testid = "technical-details-switch" }) {
  return (<span className="inline-flex text-xs text-cx-muted" data-testid={testid}>
    <Switch checked={on} onChange={onChange} label="Technical details" labelFirst size="sm" testid={`${testid}-toggle`} />
  </span>);
}

export const MeasuredBadge = ({ className }) => <span className={clsx("cx-badge cx-badge-green !py-0 !px-1.5 !text-[10px]", className)} title="Measured on this hardware" data-testid="measured-badge">measured ✓</span>;
export const EstimatedBadge = ({ className }) => <span className={clsx("cx-badge cx-badge-yellow !py-0 !px-1.5 !text-[10px]", className)} title="Estimated by the planner; measure to replace" data-testid="estimated-badge">estimated ~</span>;

/** Glossary tooltip: plain term shown, technical term + description in the tooltip. */
export function Term({ children, term }) {
  const g = GLOSSARY[term || children] || GLOSSARY[children];
  if (!g) return <>{children}</>;
  return (<TooltipPrimitive.Provider delayDuration={150}><TooltipPrimitive.Root>
    <TooltipPrimitive.Trigger asChild><span className="underline decoration-dotted decoration-cx-muted cursor-help" data-testid="glossary-term">{children}</span></TooltipPrimitive.Trigger>
    <TooltipPrimitive.Portal><TooltipPrimitive.Content className="cx-card cx-card-static !p-2 text-xs z-50 max-w-xs" sideOffset={6}><Mono className="text-cx-cyan">{g.tech}</Mono><div className="mt-1">{g.desc}</div></TooltipPrimitive.Content></TooltipPrimitive.Portal>
  </TooltipPrimitive.Root></TooltipPrimitive.Provider>);
}

export const NoData = ({ title = "No data", desc, action, testid = "no-data" }) => (
  <div className="rounded-cx border border-dashed border-cx-border p-4 text-center text-xs text-cx-muted" data-testid={testid}>
    <div className="inline-flex items-center gap-1 font-medium text-cx-text/80"><Info size={12} /> {title}</div>
    {desc && <div className="mt-1">{desc}</div>}
    {action && <div className="mt-2">{action}</div>}
  </div>
);

export const barTone = (pct) => (pct == null ? "bg-cx-border" : pct >= 90 ? "bg-cx-danger" : pct >= 70 ? "bg-cx-amber" : pct >= 50 ? "bg-cx-cyan" : "bg-cx-emerald");

/** used/max load bar coloured at 50/70/90 % */
export function LoadBar({ used, total, label, testid }) {
  const pct = total ? Math.min(100, Math.round((used / total) * 100)) : null;
  return (<div className="min-w-[110px]" data-testid={testid} title={total ? `${used} of ${total}` : "no data"}>
    <div className="h-1.5 rounded-full bg-cx-border/60 overflow-hidden"><div className={clsx("h-full rounded-full transition-[width] duration-500", barTone(pct))} style={{ width: `${pct || 0}%` }} /></div>
    <div className="text-[10px] text-cx-muted mt-0.5 tabular-nums">{label || (total ? `${used}/${total} in use` : "no data")}</div>
  </div>);
}

/** Step header for wizards */
export function WizardSteps({ steps, current, testid = "wizard-steps" }) {
  return (<div className="flex flex-wrap gap-4 mb-6" data-testid={testid}>
    {steps.map((s, i) => { const n = i + 1; const active = n === current; const done = n < current;
      return (<div key={s} className={clsx("flex items-center gap-2 text-sm", active ? "text-cx-emerald" : done ? "text-cx-text" : "text-cx-muted")} data-testid={`${testid}-${n}`} data-state={active ? "active" : done ? "done" : "todo"}>
        <span className={clsx("w-6 h-6 rounded-full grid place-items-center text-xs border", active ? "border-cx-emerald" : done ? "border-cx-emerald/50 bg-cx-emerald/10" : "border-cx-border")}>{done ? "✓" : n}</span>{s}
      </div>); })}
  </div>);
}

/** Imperative confirmation for legacy pages: `await askConfirm({ title, impact, word })` → boolean. Host is mounted in Shell. */
let _ask = null;
export const askConfirm = (opts) => (_ask ? _ask(opts) : Promise.resolve(false));
export function ConfirmHost() {
  const [st, setSt] = useState(null);
  useEffect(() => { _ask = (opts) => new Promise((resolve) => setSt({ ...opts, resolve })); return () => { _ask = null; }; }, []);
  if (!st) return null;
  const close = (v) => { st.resolve(v); setSt(null); };
  return st.word ? <ConfirmTyped open title={st.title} desc={st.impact} word={st.word} onOpenChange={() => close(false)} onConfirm={() => close(true)} confirmLabel={st.confirmLabel || "Confirm"} testid="confirm-typed" />
    : <ConfirmImpact open title={st.title} impact={st.impact} onOpenChange={() => close(false)} onConfirm={() => close(true)} confirmLabel={st.confirmLabel || "Confirm"} danger={st.danger !== false} testid="confirm-impact" />;
}
